import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const [form, setForm] = useState({ full_name: '', date_of_birth: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.date_of_birth || !form.phone || !form.email) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers`, form);
      setSubmitted(true);
      toast.success('Application submitted! We will be in touch.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0A0A0A]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-neutral-300 hover:text-red-500 transition-colors" data-testid="careers-back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1iyuca53_IMG_9172.png"
              alt="MarcoPolo Logo"
              className="w-10 h-10 object-contain rounded-full ring-1 ring-red-500/40"
            />
            <span className="font-display font-extrabold tracking-tight">MarcoPolo</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-red-600/15 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-red-500/30">
            <Briefcase className="w-4 h-4" />
            We&apos;re Hiring
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
            Join Our Team
          </h1>
          <p className="text-lg text-neutral-400 leading-relaxed">
            Help us provide safe, reliable medical transportation across Washington State.
            Apply below and our team will reach out to you.
          </p>
        </div>

        {submitted ? (
          <div className="bg-[#171717] border border-white/10 rounded-2xl p-10 text-center" data-testid="careers-success">
            <div className="w-16 h-16 bg-red-600/15 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-display text-2xl font-bold text-white mb-3">Application Received</h2>
            <p className="text-neutral-400 mb-8">
              Thank you for applying, {form.full_name.split(' ')[0] || 'there'}! We&apos;ve received your application
              and will contact you soon.
            </p>
            <Link to="/">
              <Button className="bg-red-600 hover:bg-red-500 text-white rounded-full px-8 py-6 text-lg font-semibold">
                Back to Home
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#171717] border border-white/10 rounded-2xl p-6 sm:p-8 space-y-6" data-testid="careers-form">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-neutral-300">Full Name</Label>
              <Input
                id="full_name"
                data-testid="careers-fullname"
                value={form.full_name}
                onChange={(e) => update('full_name', e.target.value)}
                placeholder="Jane Doe"
                className="h-12 bg-[#0A0A0A] border-white/15 text-white placeholder:text-neutral-500 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="text-neutral-300">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                data-testid="careers-dob"
                value={form.date_of_birth}
                onChange={(e) => update('date_of_birth', e.target.value)}
                className="h-12 bg-[#0A0A0A] border-white/15 text-white placeholder:text-neutral-500 focus:border-red-500 focus:ring-red-500 [color-scheme:dark]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-neutral-300">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                data-testid="careers-phone"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="+1 (509) 712-7154"
                className="h-12 bg-[#0A0A0A] border-white/15 text-white placeholder:text-neutral-500 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-300">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="careers-email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="jane@example.com"
                className="h-12 bg-[#0A0A0A] border-white/15 text-white placeholder:text-neutral-500 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="careers-submit-btn"
              className="w-full bg-red-600 hover:bg-red-500 text-white rounded-full py-6 text-lg font-semibold shadow-lg shadow-red-900/40 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
